// src/pages/SubService.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  CircularProgress,
  Box,
  Alert,
} from "@mui/material";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";

export default function SubService() {
  const { serviceId } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!serviceId) return;
    setLoading(true);
    setError(null);

    const fetchSub = async () => {
      try {
        const colRef = collection(db, "services", serviceId, "subservices");
        // If you don't have 'priority' remove the orderBy line
        const q = query(colRef, orderBy("priority", "asc"));
        const snap = await getDocs(q);
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems(arr);
      } catch (err) {
        console.error(err);
        setError("Failed to load subservices. Check Firestore rules & network.");
      } finally {
        setLoading(false);
      }
    };

    fetchSub();
  }, [serviceId]);

  return (
    <Container
        maxWidth="xl"
        sx={{
          pt: { xs: 10, md: 14 },
          pb: 10,
          minHeight: "100vh",
          color: "#2B211C",
          bgcolor: "#F7F4EF",
        }}
      >
      <Box mb={3}>
        <Button component={RouterLink} to="/services" variant="text">← Back to Services</Button>
      </Box>

      <Typography variant="h4" mb={2} fontFamily="Georgia, 'Times New Roman', serif" fontWeight="bold">
        {serviceId?.replace(/-/g, " ")}
      </Typography>

      {loading && (
        <Box textAlign="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && items.length === 0 && (
        <Typography color="text.secondary">No subservices found for this service.</Typography>
      )}

      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mt: 3 }}>
        {items.map((it) => (
          <Grid item xs={12} sm={6} md={4} key={it.id}>
            <Card
                sx={{
                  height: "100%",
                  borderRadius: "2px",
                  overflow: "hidden",
                  border: "1px solid #DCD4CC",
                  backgroundColor: "#FCFAF6",
                  boxShadow: "0 18px 50px rgba(43,33,28,0.08)",
                  transition: "transform .3s ease, box-shadow .3s ease",
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0 24px 60px rgba(43,33,28,0.13)",
                  },
                }}
              >
              {it.imageUrl && <CardMedia component="img" height="240" image={it.imageUrl} alt={it.title || it.name} />}
              <CardContent>
                <Typography variant="h6" mb={1}>{it.title || it.name}</Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>{it.description || it.desc}</Typography>
                {it.ctaUrl ? (
                  <Button href={it.ctaUrl} target="_blank" rel="noreferrer" variant="contained" sx={{ bgcolor: "#2B211C" }}>
                    Learn More
                  </Button>
                ) : (
                  <Button component={RouterLink} to={`/services/${serviceId}/${it.id}`} variant="outlined">
                    Details
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
